'use client';

import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { BiRocket, BiTime, BiTrendingUp } from 'react-icons/bi';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { RiFilePaper2Line, RiSparkling2Line } from 'react-icons/ri';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import ChatbotsNewFeatures from '@/components/blocks/ChatbotsNewFeatures';
import BlockDescriptionImageRight from '@/components/blocks/BlockDescriptionImageRight';
import BlockDescriptionImageLeft from '@/components/blocks/BlockDescriptionImageLeft';
import ReadyTransformCustomerExperience from '@/components/blocks/ReadyTransformCustomerExperience';
import SectionIntegration from '@/components/sectionIntegration/SectionIntegration';
import React from 'react';
import { TbTargetArrow } from 'react-icons/tb';
import { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import { HiOutlineChip, HiOutlineShieldCheck } from 'react-icons/hi';
import GeneralChatbotBenefits from '@/components/blocks/GeneralChatbotBenefits';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const images = [
    'https://cdn.1cdn.app/application/LOUISEBOT/24031212385190_Textscenario-page-01.mp4',
    'https://cdn.1cdn.app/application/LOUISEBOT/24031212385155_Textscenario-page-02.mp4',
    'https://cdn.1cdn.app/application/LOUISEBOT/2403121238516_Textscenario-page-03.mp4',
  ];

  const hero = {
    content: {
      badge: t('features/textScenario/hero-badge'),
      title: t('features/textScenario/hero-title'),
      description: t('features/textScenario/hero-subtitle'),
      cta: t('features/textScenario/hero-cta'),
      btnDemoText: t('features/textScenario/watch-demo'),
      trustedBy: t('features/textScenario/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071516115485_textbot_hero.png',
      icon: TbTargetArrow,
      imgOrientation: 'portrait' as IImageOrientation,
      ctaTarget: 'register' as CtaTargetType,
      btnDemoTarget: 'discover-chatbot-text' as WatchButtonCtaTargetType,
    },
    benefits: [
      { icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />, text: t('features/textScenario/hero-benefit1') },
      { icon: <TbTargetArrow className="tw-w-5 tw-h-5" />, text: t('features/textScenario/hero-benefit2') },
    ],
    stats: [
      { value: '100%', label: t('features/textScenario/focus1') },
      { value: '35+', label: t('features/textScenario/focus2') },
      { value: '95%', label: t('features/textScenario/focus3') },
    ],
  };

  const sections = t('features/textScenario/WhyChooseTextBot/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = {
    section: {
      title: t('features/textScenario/WhyChooseTextBot/title'),
      subtitle: t('features/textScenario/WhyChooseTextBot/subtitle'),
      description: t('features/textScenario/WhyChooseTextBot/description'),
    },
    argument: [
      {
        iconicText: t(sections[0].improvement),
        title: t(sections[0].title),
        description: t(sections[0].description),
        icon: <BiTrendingUp className="tw-w-10 tw-h-10 tw-text-white" />,
        outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
        metricLabel: t(sections[0].metricLabel),
        button: t(sections[0].button),
        metric: t(sections[0].metric),
        colorFrom: 'pink' as ColorDefaultChoice,
        colorTo: 'purple' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[1].improvement),
        title: t(sections[1].title),
        description: t(sections[1].description),
        icon: <BiTime className="tw-w-10 tw-h-10 tw-text-white" />,
        outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
        metricLabel: t(sections[1].metricLabel),
        button: t(sections[1].button),
        metric: t(sections[1].metric),
        colorFrom: 'blue' as ColorDefaultChoice,
        colorTo: 'cyan' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[2].improvement),
        title: t(sections[2].title),
        description: t(sections[2].description),
        icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
        outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
        metricLabel: t(sections[2].metricLabel),
        button: t(sections[2].button),
        metric: t(sections[2].metric),
        colorFrom: 'green' as ColorDefaultChoice,
        colorTo: 'emerald' as ColorDefaultChoice,
      },
    ],
  };

  const footerSectionBlock = t('features/textScenario/NewFeaturesForBuildingTextbots/footerSectionBlock', {
    returnObjects: true,
  }) as Array<any>;

  const newFeatures = {
    section: {
      badge: t('features/textScenario/NewFeaturesForBuildingTextbots/badge'),
      title: t('features/textScenario/NewFeaturesForBuildingTextbots/title'),
      description: t('features/textScenario/NewFeaturesForBuildingTextbots/description'),
      subtitle: t('features/textScenario/NewFeaturesForBuildingTextbots/subtitle'),
      badgeCatchEye: {
        title: t('features/textScenario/NewFeaturesForBuildingTextbots/badgeCatchEye/title'),
        description: t('features/textScenario/NewFeaturesForBuildingTextbots/badgeCatchEye/description'),
      },
      footerSectionBlock: footerSectionBlock,
    },
    features: [
      {
        title: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature1/title'),
        description: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature1/description'),
        subtitle: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature1/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071121045199_js_hook.png',
        icon: <RiFilePaper2Line className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature2/title'),
        description: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature2/description'),
        subtitle: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature2/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071121045261_scenario_and_ai.png',
        icon: <HiOutlineChip className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature3/title'),
        description: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature3/description'),
        subtitle: t('features/textScenario/NewFeaturesForBuildingTextbots/features/feature3/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071121045258_compliance.png',
        icon: <HiOutlineShieldCheck className="tw-w-6 tw-h-6 tw-text-white" />,
      },
    ],
  };

  return (
    <div>
      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={whyChooseThisBot.section.title}
            subtitle={whyChooseThisBot.section.subtitle}
            description={whyChooseThisBot.section.description}
            sections={whyChooseThisBot.argument}
          />

          <ChatbotsNewFeatures
            features={newFeatures.features}
            subtitle={newFeatures.section.subtitle}
            description={newFeatures.section.description}
            title={newFeatures.section.title}
            badge={newFeatures.section.badge}
            badgeCatchEye={newFeatures.section.badgeCatchEye}
            footerSectionBlock={newFeatures.section.footerSectionBlock}
            locale={locale as SupportedLanguages}
          />
        </section>

        {/* All other sections with containers */}
        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <div className="tw-space-y-10">
              {/* Simple Setup */}
              <BlockDescriptionImageRight
                badge={t('features/textScenario/section-bloc1/badge')}
                badgeIcon={<RiSparkling2Line className="tw-w-4 tw-h-4" />}
                title={t('features/textScenario/section-bloc1/title')}
                description={t('features/textScenario/section-bloc1/description')}
                btnColor="blue"
                cta={t('features/textScenario/section-bloc1/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25071119575978_live.png'}
                link="register"
                locale={locale}
              />

              {/* Templates */}
              <BlockDescriptionImageLeft
                title={t('features/textScenario/section-bloc2/title')}
                description={t('features/textScenario/section-bloc2/description')}
                btnColor="green"
                cta={t('features/textScenario/section-bloc2/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25071120022139_operation.png'}
                link="register"
                locale={locale}
              />

              {/* No Coding */}
              <BlockDescriptionImageRight
                title={t('features/textScenario/section-bloc3/title')}
                description={t('features/textScenario/section-bloc3/description')}
                btnColor="blue"
                cta={t('features/textScenario/section-bloc3/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/2507111957592_complexity.png'}
                link="register"
                locale={locale}
              />

              <BlockDescriptionImageLeft
                title={t('features/textScenario/section-bloc4/title')}
                description={t('features/textScenario/section-bloc4/description')}
                btnColor="green"
                cta={t('features/textScenario/section-bloc4/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25071120201865_expert.png'}
                link="register"
                locale={locale}
              />
            </div>
          </div>
        </section>

        <GeneralChatbotBenefits locale={locale} />

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <ReadyTransformCustomerExperience locale={locale} />
        </section>

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <SectionIntegration locale={locale} />
        </section>

        <SiteFrequentlyAskedQuestionsComponent locale={locale} session="TextBotFAQs" />
      </AnimatedGradientWrapper>
    </div>
  );
}
